// @flow
import React, { useContext } from 'react';
import { getIn, useFormikContext } from 'formik';
import { useParams } from 'react-router-dom';
import { InfoMessageContainer } from '@presentational/messages/InfoMessageContainer';

import {
  CLUSTER_TABLEFLOW_FORM,
  CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG,
  CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME,
  LEGACY_TABLEFLOW_VERSION_MESSAGE,
  TABLEFLOW_STORAGE_TYPE_CONFLUENT,
  TABLEFLOW_STORAGE_TYPE_OWN,
  TABLEFLOW_VERSIONS,
} from '../../../constants';
import { ClusterInputTable } from '../cluster-input-table/ClusterInputTable';
import { PaddedAndRaisedSegment } from '../../presentational/PaddedAndRaisedSegment';
import { toastError } from '../../presentational/notifications/utils';
import { useUpdateClusterMutation } from '../../../service-definitions/estimates';
import { EstimateContext } from '../../../contexts/EstimateContext';
import { isAnythingEditedOnClusterPageBesidesTheGivenForm } from '../../views/utils';
import { SelectField } from '../../../formik-utils/FormFields';
import { Spacer } from '../../presentational/spacing/Spacer';
import { StyledSegmentWithBorderColor } from '../../../common-utils/styledComponents';

import { TABLEFLOW_ROWS_CONFIG_BY_VERSION } from './tableflow-config';

// Legacy (v1) only -- see TableflowV2UsageContainer for the v2 tab.
export const TableflowUsageContainer = () => {
  const rowsConfig = TABLEFLOW_ROWS_CONFIG_BY_VERSION[TABLEFLOW_VERSIONS.LEGACY];

  const storageTypeOptions = [
    {
      name: TABLEFLOW_STORAGE_TYPE_CONFLUENT,
      display_name: 'Enable Confluent Storage',
    },
    {
      name: TABLEFLOW_STORAGE_TYPE_OWN,
      display_name: 'Use my own storage',
    },
  ];
  const { estimateId, clusterId } = useParams();
  const [updateCluster] = useUpdateClusterMutation();
  const estimate = useContext(EstimateContext);

  const { values, initialValues } = useFormikContext();
  const shouldFormBeDisabled = isAnythingEditedOnClusterPageBesidesTheGivenForm(
    values,
    initialValues,
    CLUSTER_TABLEFLOW_FORM
  );

  return (
    <PaddedAndRaisedSegment disabled={shouldFormBeDisabled}>
      <InfoMessageContainer message={LEGACY_TABLEFLOW_VERSION_MESSAGE} />
      <ClusterInputTable
        AdditionalComponent={
          <>
            <Spacer y={40} />
            <StyledSegmentWithBorderColor compact={true}>
              <SelectField
                disabled={shouldFormBeDisabled}
                fieldDisplayName="Storage Type"
                fieldName={`${CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG}.${CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME}`}
                fluid={false}
                options={storageTypeOptions}
                search={false}
              />
            </StyledSegmentWithBorderColor>
          </>
        }
        disabled={shouldFormBeDisabled}
        keyNameInValues={CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG}
        rowsConfig={rowsConfig}
        shouldDisableMonthWhenCorrespondingKafkaMonthIsDisabled={true}
        tableName="Tableflow Monthly Usage"
        updateFunc={async () => {
          const valuesToSendToBackend = getIn(values, CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG);
          const storageType = valuesToSendToBackend[CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME];
          const tableflowInputs = Object.values(valuesToSendToBackend.months);
          const payload = {
            id: clusterId,
            estimate_id: estimateId,
            estimate_version: estimate?.inputs?.estimate_version,
            [CLUSTER_TABLEFLOW_MONTHLY_INPUTS_JSON_CONFIG]: {
              [CLUSTER_TABLEFLOW_STORAGE_TYPE_BACKEND_NAME]: storageType,
              inputs: tableflowInputs,
            },
          };
          const { error } = await updateCluster({
            estimateId,
            clusterId,
            payload,
          });
          if (error) {
            toastError(error);
          }
        }}
      />
    </PaddedAndRaisedSegment>
  );
};
